import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Form from '@rjsf/mui';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { useSnackbar } from 'notistack';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Box, Button, Stack } from '@mui/material';

import { RoomPolicy } from '~/types/roomPolicy';

import OffersService from '../../../services/OffersService';
import parseRoomPolicyDescription from '../../../utils/parseRoomPolicyDescription';
import { reportError } from '../../../utils/reportError';
import ErrorListDisplay from '../ErrorListDisplay';

import RoomPolicyFormTextWidget from './RoomPolicyFormWidget';
import RoomPolicyTableEditor from './RoomPolicyTableEditor';
import { buttonMessages, buttonStates } from './states/submitButton';
import MarkdownEditor from './widgets/MarkdownEditor';

interface RoomPolicyFormProps {
  schema: RJSFSchema;
  roomPolicy: RoomPolicy;
  idOffer: string;
  offerType: string;
}

export default function RoomPolicyForm({
  schema: propsSchema,
  roomPolicy: initialRoomPolicy,
  idOffer,
  offerType,
}: RoomPolicyFormProps) {
  const { enqueueSnackbar } = useSnackbar();
  const canSyncRoomPolicy = offerType === 'hotel';

  const [apiErrors, setApiErrors] = useState([]);
  const [roomPolicy, setRoomPolicy] = useState(initialRoomPolicy);
  const [saveState, setSaveState] = useState(buttonStates.default);
  const [isGenerating, setIsGenerating] = useState(false);
  const [textDescriptionBackup, setTextDescriptionBackup] = useState<string>(() => {
    const initialDescription = initialRoomPolicy?.description ?? '';
    const initialParsed = parseRoomPolicyDescription(initialDescription);
    return initialParsed.isStructured ? '' : initialDescription;
  });

  // Parse from current state so it updates after generate
  const parsedDescription = useMemo(
    () => parseRoomPolicyDescription(roomPolicy?.description),
    [roomPolicy?.description],
  );

  // When in structured mode, hold policy data in form state so "Sync Room Policy" can replace it and table stays in sync
  const [structuredPolicyData, setStructuredPolicyData] = useState(() =>
    parsedDescription.isStructured ? parsedDescription.data : null,
  );

  useEffect(() => {
    setRoomPolicy(initialRoomPolicy);
    const nextDescription = initialRoomPolicy?.description ?? '';
    const nextParsed = parseRoomPolicyDescription(nextDescription);
    setTextDescriptionBackup(nextParsed.isStructured ? '' : nextDescription);
  }, [initialRoomPolicy]);

  // Sync structured policy data whenever description changes (e.g. after "Sync Room Policy")
  useEffect(() => {
    if (parsedDescription.isStructured) {
      setStructuredPolicyData(parsedDescription.data);
    } else {
      setStructuredPolicyData(null);
    }
  }, [parsedDescription]);

  const onChange = useCallback(
    (edit) => {
      setRoomPolicy(edit.formData);
      if (saveState !== buttonStates.default) {
        setSaveState(buttonStates.default);
      }
    },
    [saveState],
  );

  const onSubmit = useCallback(
    async (form) => {
      form.errors = [];
      form.errorSchema = {};

      setSaveState(buttonStates.saving);

      const submitData = JSON.parse(JSON.stringify(form.formData));
      const idRoomPolicy = submitData.id;
      delete submitData['room_type_name'];
      delete submitData['id'];
      delete submitData['salesforce_id'];
      delete submitData['property_id'];
      delete submitData['room_rate_id'];
      delete submitData['room_rate_ids'];

      try {
        if (idRoomPolicy) {
          delete submitData['room_type_id'];
          await OffersService.updateRoomPolicy(submitData, idOffer, idRoomPolicy);
        } else {
          await OffersService.createRoomPolicy(submitData, idOffer);
        }
        setSaveState(buttonStates.saved);
        setApiErrors([]);
        enqueueSnackbar('Room policy saved successfully', { variant: 'success' });
      } catch (e) {
        const newApiErrors = [];
        if (e.name === 'ValidationError') {
          e.errors.forEach((err) => {
            newApiErrors.push(err.message);
          });
        }
        setSaveState(buttonStates.failed);
        setApiErrors(newApiErrors);
        reportError(e);
        enqueueSnackbar('Failed to save room policy', { variant: 'error' });
      }
    },
    [enqueueSnackbar, idOffer],
  );

  const onGenerateRoomPolicy = useCallback(async () => {
    setIsGenerating(true);
    try {
      const response = await OffersService.generateRoomPolicy(
        initialRoomPolicy.salesforce_id,
        initialRoomPolicy.property_id,
        initialRoomPolicy.room_type_id,
        initialRoomPolicy.room_rate_id,
        initialRoomPolicy.room_rate_ids,
      );

      const generatedData = response.result.content;
      const currentDescription = roomPolicy?.description ?? '';
      if (!parseRoomPolicyDescription(currentDescription).isStructured) {
        setTextDescriptionBackup(currentDescription);
      }
      setRoomPolicy((prev) => ({
        ...prev,
        description: JSON.stringify(generatedData),
      }));
      setSaveState(buttonStates.default);
      enqueueSnackbar('Room policy synced successfully', { variant: 'success' });
    } catch (e) {
      reportError(e);
      enqueueSnackbar('Failed to sync room policy', { variant: 'error' });
    } finally {
      setIsGenerating(false);
    }
  }, [initialRoomPolicy, enqueueSnackbar, roomPolicy?.description]);

  const onRevertToTextInput = useCallback(() => {
    setStructuredPolicyData(null);
    setRoomPolicy((prev) => ({
      ...prev,
      description: textDescriptionBackup ?? '',
    }));
    if (saveState !== buttonStates.default) {
      setSaveState(buttonStates.default);
    }
  }, [saveState, textDescriptionBackup]);

  const handleStructuredPolicyChange = useCallback((nextData) => {
    setStructuredPolicyData(nextData);
    setRoomPolicy((prev) => ({ ...prev, description: JSON.stringify(nextData) }));
  }, []);

  const validate = useCallback((formData, errors) => {
    return errors;
  }, []);

  // Strip extra fields that aren't in the API schema to avoid "additionalProperties" validation errors
  const formData = useMemo(() => {
    if (!roomPolicy) {
      return {};
    }
    const data = { ...roomPolicy };
    delete data.property_id;
    delete data.room_rate_id;
    delete data.room_rate_ids;
    return data;
  }, [roomPolicy]);

  // Render structured table editor when description is valid JSON (controlled from form state so "Sync Room Policy" updates table)
  if (parsedDescription.isStructured) {
    const dataToShow = structuredPolicyData ?? parsedDescription.data;
    if (dataToShow) {
      return (
        <RoomPolicyTableEditor
          policyData={dataToShow}
          onPolicyDataChange={handleStructuredPolicyChange}
          roomPolicy={roomPolicy}
          idOffer={idOffer}
          onGenerateDefault={onGenerateRoomPolicy}
          isGenerating={isGenerating}
          onRevertToTextInput={onRevertToTextInput}
        />
      );
    }
  }

  const schema: RJSFSchema = {
    ...propsSchema,
    properties: {
      id: {
        title: 'Room Policy ID',
        type: ['integer', 'null'],
      },
      salesforce_id: {
        title: 'Salesforce ID',
        type: ['string', 'null'],
      },
      ...propsSchema.properties,
      room_type_name: {
        title: 'Room Type Name',
        type: ['string', 'null'],
      },
    },
  };

  const uiSchema = {
    room_type_id: { 'ui:widget': 'hidden' },
    id: { 'ui:widget': 'hidden' },
    salesforce_id: { 'ui:widget': 'hidden' },
    room_type_name: { 'ui:widget': 'hidden' },
    description: { 'ui:widget': MarkdownEditor },
  };

  let apiErrorsDisplay = null;

  if (apiErrors?.length) {
    apiErrorsDisplay = <ErrorListDisplay messages={apiErrors} />;
  }

  return (
    <Box>
      <Form
        schema={schema}
        formData={formData}
        validator={validator}
        showErrorList={false}
        liveValidate
        customValidate={validate}
        onSubmit={onSubmit}
        onChange={onChange}
        uiSchema={uiSchema}
        widgets={{
          TextWidget: RoomPolicyFormTextWidget,
        }}
        formContext={{
          hideLabel: true,
        }}
      >
        {apiErrorsDisplay}
        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained" className={saveState}>
            {buttonMessages[saveState]}
          </Button>
          {canSyncRoomPolicy && (
            <Button
              variant="contained"
              startIcon={<AutoFixHighIcon />}
              onClick={onGenerateRoomPolicy}
              disabled={isGenerating}
            >
              {isGenerating ? 'Syncing...' : 'Sync Room Policy'}
            </Button>
          )}
        </Stack>
      </Form>
    </Box>
  );
}
